import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { 
  ArrowLeft, 
  User, 
  Settings, 
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Loader
} from 'lucide-react';

const PruebaUserV7 = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profileCreated, setProfileCreated] = useState(false);
  const [testUsers, setTestUsers] = useState([]);
  const [logs, setLogs] = useState([]);

  // Verificar que solo demo@demo.com pueda acceder
  useEffect(() => {
    if (user?.email !== 'demo@demo.com') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const addLog = (message) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const createProfile = async () => {
    setLoading(true);
    setLogs([]);
    addLog('🔧 Iniciando creación de perfil pruebav7...');
    
    try {
      const response = await api.post('/api/debug/create-profile-pruebav7');
      addLog('✅ ' + response.data.message);
      addLog('📝 ' + response.data.details);
      setProfileCreated(true);
    } catch (error) {
      addLog('❌ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const createUserWithMethod = async (method) => {
    setLoading(true);
    const methodLogs = [];
    
    try {
      methodLogs.push(`🚀 Probando Método ${method}...`);
      setLogs(methodLogs);
      
      const response = await api.post(`/api/debug/create-user-v7-method/${method}`);
      
      // Agregar logs del servidor
      if (response.data.logs) {
        response.data.logs.forEach(log => methodLogs.push(log));
      }
      
      methodLogs.push('');
      methodLogs.push('📊 RESULTADO:');
      methodLogs.push(`Usuario: ${response.data.username}`);
      methodLogs.push(`Password: ${response.data.password}`);
      methodLogs.push(`Limit Uptime: ${response.data.limit_uptime_set || 'NO CONFIGURADO ❌'}`);
      
      if (response.data.limit_uptime_set) {
        methodLogs.push('');
        methodLogs.push('🎉 ¡ÉXITO! El método ' + method + ' FUNCIONA');
      } else {
        methodLogs.push('');
        methodLogs.push('⚠️ El método ' + method + ' NO configuró limit-uptime');
      }
      
      setLogs(methodLogs);
      
      // Recargar lista
      await loadUsers();
      
    } catch (error) {
      methodLogs.push('❌ Error: ' + (error.response?.data?.detail || error.message));
      setLogs(methodLogs);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/api/debug/list-test-users-v7');
      setTestUsers(response.data.users);
    } catch (error) {
      console.error('Error loading test users:', error);
    }
  };

  const deleteAllUsers = async () => {
    if (!window.confirm('¿Eliminar TODOS los usuarios de prueba?')) return;
    
    setLoading(true);
    addLog('🗑️ Eliminando usuarios de prueba...');
    
    try {
      const response = await api.delete('/api/debug/delete-all-test-users-v7');
      addLog('✅ ' + response.data.message);
      setTestUsers([]);
    } catch (error) {
      addLog('❌ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  if (user?.email !== 'demo@demo.com') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al Dashboard
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🧪 Pruebas de Usuarios V7
          </h1>
          <p className="text-gray-600">
            Página de pruebas para verificar la inyección de limit-uptime en RouterOS v7
          </p>
          <p className="text-sm text-purple-600 mt-2">
            Solo accesible para: demo@demo.com
          </p>
        </div>

        {/* Paso 1: Crear Perfil */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 text-purple-600" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Paso 1: Crear Perfil "pruebav7"</h2>
                <p className="text-sm text-gray-600">Perfil de 1 minuto pausado para testing</p>
              </div>
            </div>
            {profileCreated && <CheckCircle className="w-8 h-8 text-green-500" />}
          </div>
          
          <button
            onClick={createProfile}
            disabled={loading}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Settings className="w-5 h-5" />}
            Crear Perfil pruebav7
          </button>
        </div>

        {/* Paso 2: Probar Métodos */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <User className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Paso 2: Probar Métodos de Creación</h2>
              <p className="text-sm text-gray-600">Cada método prueba una forma diferente de inyectar limit-uptime</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <button
              onClick={() => createUserWithMethod(1)}
              disabled={loading}
              className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border-2 border-blue-200 text-left disabled:opacity-50"
            >
              <div className="font-bold text-blue-900 mb-2">Método 1</div>
              <div className="text-sm text-blue-700">limit_uptime</div>
              <div className="text-xs text-gray-600">(guion bajo)</div>
            </button>
            
            <button
              onClick={() => createUserWithMethod(2)}
              disabled={loading}
              className="p-4 bg-green-50 hover:bg-green-100 rounded-lg border-2 border-green-200 text-left disabled:opacity-50"
            >
              <div className="font-bold text-green-900 mb-2">Método 2</div>
              <div className="text-sm text-green-700">limit-uptime</div>
              <div className="text-xs text-gray-600">(guion normal)</div>
            </button>
            
            <button
              onClick={() => createUserWithMethod(3)}
              disabled={loading}
              className="p-4 bg-orange-50 hover:bg-orange-100 rounded-lg border-2 border-orange-200 text-left disabled:opacity-50"
            >
              <div className="font-bold text-orange-900 mb-2">Método 3</div>
              <div className="text-sm text-orange-700">CREATE + SET</div>
              <div className="text-xs text-gray-600">(dos pasos)</div>
            </button>
          </div>
        </div>

        {/* Logs */}
        {logs.length > 0 && (
          <div className="bg-gray-900 rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">📋 Logs de Ejecución</h2>
            <div className="bg-black rounded p-4 font-mono text-sm text-green-400 overflow-auto max-h-96">
              {logs.map((log, index) => (
                <div key={index} className="mb-1">{log}</div>
              ))}
            </div>
          </div>
        )}

        {/* Lista de Usuarios */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <User className="w-6 h-6 text-indigo-600" />
              <h2 className="text-xl font-bold text-gray-900">Usuarios de Prueba Creados</h2>
            </div>
            <div className="flex gap-2">
              <button
                onClick={loadUsers}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCw className="w-4 h-4" />
                Recargar
              </button>
              <button
                onClick={deleteAllUsers}
                disabled={loading || testUsers.length === 0}
                className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                Eliminar Todos
              </button>
            </div>
          </div>

          {testUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay usuarios de prueba creados
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Usuario</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Perfil</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Limit Uptime</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Uptime</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {testUsers.map((user, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{user.profile}</td>
                      <td className="px-4 py-3 text-sm">
                        {user.limit_uptime === 'NO CONFIGURADO' ? (
                          <span className="text-red-600 font-semibold flex items-center gap-1">
                            <XCircle className="w-4 h-4" />
                            NO CONFIGURADO
                          </span>
                        ) : (
                          <span className="text-green-600 font-semibold flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            {user.limit_uptime}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{user.uptime}</td>
                      <td className="px-4 py-3 text-sm">
                        {user.comment && (
                          <span className="text-xs text-gray-500">{user.comment}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Instrucciones */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 mt-6">
          <h3 className="font-bold text-yellow-900 mb-3">📖 Instrucciones de Uso</h3>
          <ol className="space-y-2 text-sm text-yellow-800">
            <li><strong>1.</strong> Primero crea el perfil "pruebav7" haciendo clic en el botón azul</li>
            <li><strong>2.</strong> Luego prueba cada método (1, 2, 3) para ver cuál configura correctamente limit-uptime</li>
            <li><strong>3.</strong> Verifica en Winbox → IP → Hotspot → Users → pestaña "Limits"</li>
            <li><strong>4.</strong> El método exitoso mostrará "Limit Uptime: 00:01:00" en verde</li>
            <li><strong>5.</strong> Una vez encontrado el método correcto, lo aplicaremos al sistema principal</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default PruebaUserV7;
